import { Observable } from "rxjs";
import { map, mapTo } from "rxjs/operators";
import { HouseholdClaimRepository } from "src/containers/household-claim/repositories/claim/household-claim.repository";
import { inject, injectable } from "tsyringe";
import type {
    RemoveArguments,
    UploadArguments,
} from "src/containers/household-claim/types";
import { HouseholdClaimCache } from "src/containers/household-claim/models/claim-cache.model";
import { HouseholdClaim } from "src/containers/household-claim/models/claim.model";
import { HouseholdClaimDocumentsTree } from "src/containers/household-claim/models/documents-tree.model";
import { HouseholdClaimBooking } from "src/containers/household-claim/models/household-claim-booking.model";
import { HouseholdClaimDecisionTreeModel } from "src/containers/household-claim/models/household-claim-decision-tree.model";
import { HouseholdClaimAdjuster } from "src/containers/household-claim/models/adjuster.model";
import { HouseholdClaimBookingResponse } from "src/containers/household-claim/models/booking-response.model";
import { HouseholdClaimTrackingDigital } from "src/containers/household-claim/models/tracking.model";
import { autoBind } from "src/shared/decorators/autobind";
import { GenericSuccessResponse } from "src/infra/http/types";
import { HouseholdClaimAssets } from "src/containers/household-claim/models/assets.model";
import { HouseholdClaimDocumentsUploadResponse } from "src/containers/household-claim/models/upload-response.model";
import { UpdateClaimCacheReponse } from "../../models/update-claim-cache-response.model";

@injectable()
export class HouseholdClaimService {
    constructor(
        @inject("HouseholdClaimRepository")
        private repository: HouseholdClaimRepository
    ) {}

    @autoBind
    getClaims(): Observable<HouseholdClaim[]> {
        return this.repository
            .getClaims()
            .pipe(
                map((claim) =>
                    claim.map((item) =>
                        HouseholdClaim.fromJson<HouseholdClaim>(item)
                    )
                )
            );
    }

    @autoBind
    getClaim(claimId: number): Observable<HouseholdClaimCache> {
        return this.repository
            .getClaim(claimId)
            .pipe(map((data) => HouseholdClaimCache.fromJson(data)));
    }

    @autoBind
    deleteClaim(claimId: number): Observable<GenericSuccessResponse> {
        return this.repository
            .deleteClaim(claimId)
            .pipe(mapTo({ code: 200, status: "ok" }));
    }

    @autoBind
    bookClaim(
        userIdentity: string,
        booking: HouseholdClaimBooking
    ): Observable<HouseholdClaimBookingResponse> {
        return this.repository
            .bookClaim(userIdentity, booking)
            .pipe(
                map((response) =>
                    HouseholdClaimBookingResponse.fromJson(response)
                )
            );
    }

    @autoBind
    getDocumentsTreeByCoverage(
        userIdentity: string,
        coverageIds: number[],
        productCode: string,
        claimId: string
    ): Observable<HouseholdClaimDocumentsTree[]> {
        return this.repository
            .getDocumentsTreeByCoverage(
                userIdentity,
                coverageIds,
                productCode,
                claimId
            )
            .pipe(
                map((data) =>
                    data.map((item) =>
                        HouseholdClaimDocumentsTree.fromJson(item)
                    )
                )
            );
    }

    @autoBind
    getDecisionTree(): Observable<HouseholdClaimDecisionTreeModel[]> {
        return this.repository
            .getDecisionTree()
            .pipe(
                map((data) =>
                    data.map((item) =>
                        HouseholdClaimDecisionTreeModel.fromJson(item)
                    )
                )
            );
    }

    @autoBind
    getCache(
        identity: string,
        claimId: number
    ): Observable<HouseholdClaimCache> {
        return this.repository
            .getCache(identity, claimId)
            .pipe(map((data) => HouseholdClaimCache.fromJson(data)));
    }

    @autoBind
    saveCache(
        identity: string,
        cache: HouseholdClaimCache
    ): Observable<UpdateClaimCacheReponse> {
        return this.repository
            .saveCache(identity, cache)
            .pipe(map((data) => UpdateClaimCacheReponse.fromJson(data)));
    }

    @autoBind
    uploadDocument(
        args: UploadArguments
    ): Observable<HouseholdClaimDocumentsUploadResponse> {
        return this.repository
            .uploadDocument(args)
            .pipe(
                map((json) =>
                    HouseholdClaimDocumentsUploadResponse.fromJson<HouseholdClaimDocumentsUploadResponse>(
                        json
                    )
                )
            );
    }

    @autoBind
    removeDocument(args: RemoveArguments): Observable<GenericSuccessResponse> {
        return this.repository.removeDocument(args);
    }

    @autoBind
    getAdjusters(): Observable<HouseholdClaimAdjuster[]> {
        return this.repository
            .getAdjusters()
            .pipe(
                map((data) =>
                    data.map((item) => HouseholdClaimAdjuster.fromJson(item))
                )
            );
    }

    @autoBind
    getTrackingDigital(): Observable<HouseholdClaimTrackingDigital[]> {
        return this.repository
            .getTrackingDigital()
            .pipe(
                map((data) =>
                    data.map((item) =>
                        HouseholdClaimTrackingDigital.fromJson(item)
                    )
                )
            );
    }

    @autoBind
    fetchDocument(url: string, filename: string): Promise<File> {
        return this.repository.fetchDocument(url, filename);
    }

    @autoBind
    getBookingAssets(
        coverageIds: string[]
    ): Observable<HouseholdClaimAssets[]> {
        return this.repository
            .getBookingAssets(coverageIds)
            .pipe(
                map((data) =>
                    data.map((item) => HouseholdClaimAssets.fromJson(item))
                )
            );
    }
}
